import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import { Grid, Typography, Hidden } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import useSWR from 'swr';

import { userInfoAtom } from '~atoms';
import { useAuthorize } from '~features/authentication';
import { DeviceCommandButtonGroup } from '~features/devices';
import { OrganisationsService } from '~features/organisations';

import { AdjustedSkeleton, StyledButton } from '../../../../../components';
import { useDetailPageBackButton } from '../../../../../shared/hooks';
import { instanceName, isArray, isObject } from '../../../../../shared/utility';
import events from '../../../../../store/actions/eventServices';

const PREFIX = 'Header';

const classes = {
	root: `${PREFIX}-root`,
	buttonSpacing: `${PREFIX}-buttonSpacing`,
};

const Root = styled('div')(() => ({
	[`& .${classes.root}`]: {},

	[`& .${classes.buttonSpacing}`]: {
		maxHeight: '56px',
	},
}));

const organisationsService = new OrganisationsService();

export {};

interface HeaderProps {
	className?: string;
	buttonPlaceholder?: string;
	action?(...args: unknown[]): unknown;
	href?: string;
	data?: object;
	loading?: boolean;
}

const Header = (props: HeaderProps) => {
	const { className, data, loading } = props;
	const location = useLocation();
	const { t } = useTranslation();
	const { authorizeForOrganisation } = useAuthorize();
	const userInfo = useAtomValue(userInfoAtom);

	const navigate = useNavigate();

	const returnButton = useDetailPageBackButton(location);
	const handleBackButtonChange = () => {
		if (location.state?.backPathname) {
			navigate(location.state.backPathname);
		} else if (location?.state?.from) {
			navigate(location.state.from);
		} else {
			navigate('/item-management/items');
		}
	};

	const handleBackButton = () => {
		if (returnButton.isBackButton || !location.state?.label) {
			return t('ui.button.inline.back');
		} else {
			return location.state.label;
		}
	};

	const organisationId = (
		data as {
			itemReference?: { hubReference?: { organisationReference?: { id?: string | number } } };
		}
	)?.itemReference?.hubReference?.organisationReference?.id;
	const userOrganisationId = userInfo?.organisation?.id;
	const isAuthorizedForOrganisation = authorizeForOrganisation(organisationId);
	const { data: organisation } = useSWR(
		userOrganisationId != null ? [organisationsService.basePath, userOrganisationId] : null,
		([_, id]) => organisationsService.getOrganisationById(String(id))
	);
	const { data: partnerships } = useSWR(
		organisationId != null && userOrganisationId != null ?
			`partnerships?pageNumber=1&pageSize=50&status=all&type=all`
		:	null,
		(path) => events.get(path)
	);
	const partnershipsResults: { partnerOrganisation?: { id?: string | number } }[] =
		isArray(partnerships?.results) ? partnerships.results : [];
	const partnershipsReady = isArray(partnershipsResults);
	const hasAcceptedPartnership =
		partnershipsReady &&
		partnershipsResults.some(
			(partner) => String(partner?.partnerOrganisation?.id) === String(organisationId)
		);
	const isAllowedContractor = organisation?.allowCommands && hasAcceptedPartnership;

	const canUseCommands = isAuthorizedForOrganisation || isAllowedContractor;

	return (
		<Root className={className}>
			<Grid alignItems='flex-end' container justifyContent='space-between' spacing={3}>
				<Grid item>
					<Hidden only={['xs', 'sm']}>
						<StyledButton
							className={classes.backButton}
							onClick={handleBackButtonChange}
							startIcon={<KeyboardArrowLeftOutlinedIcon />}
							variant='inline-default'
						>
							{handleBackButton()}
						</StyledButton>
					</Hidden>
					<Typography component='h1' variant='h2'>
						{loading ?
							<AdjustedSkeleton animation='wave' width={200} />
						:	isObject(data) && instanceName(data)}
					</Typography>
				</Grid>
				{loading ?
					<AdjustedSkeleton animation='wave' width={200} />
				:	isObject(data) &&
					['bmwCar', 'vehicleTrackerCan', 'cpacBoat'].includes(data.deviceType) &&
					canUseCommands && (
						<Grid className={classes.buttonSpacing} item>
							<DeviceCommandButtonGroup itemInstance={data} sx={{ mr: 2 }} />
						</Grid>
					)
				}
			</Grid>
		</Root>
	);
};

export default Header;
